<script setup>
import mainLogo from "@images/brand/alomonshi-symbol.png"

const emit = defineEmits(["otpSuccess", "back"])
const session = useAuthSession()
const { verifyCode, panelLogin } = useRequest()
const otpNumber = ref("")
const loading = ref(false)
const resendLoading = ref(false)
const OTP_COUNTDOWN_SECONDS = 120
const countdown = ref(OTP_COUNTDOWN_SECONDS)
const canResend = ref(false)
let timer = null

const mobileNumber = computed(() => String(useCookie("mobileNumber").value || ""))
const formattedMobile = computed(() => mobileNumber.value.replace(/(\d{4})(\d{3})(\d{4})/, "$1 $2 $3"))
const formattedTime = computed(() => {
  const minutes = Math.floor(countdown.value / 60).toLocaleString("fa-IR")
  const seconds = (countdown.value % 60).toLocaleString("fa-IR", { minimumIntegerDigits: 2, useGrouping: false })
  return `${minutes}:${seconds}`
})

const startTimer = () => {
  if (timer) clearInterval(timer)
  countdown.value = OTP_COUNTDOWN_SECONDS
  canResend.value = false
  const deadline = Date.now() + OTP_COUNTDOWN_SECONDS * 1000
  timer = setInterval(() => {
    countdown.value = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
    if (countdown.value === 0) {
      clearInterval(timer)
      timer = null
      countdown.value = 0
      canResend.value = true
    }
  }, 1000)
}

const submitForm = async () => {
  if (loading.value || String(otpNumber.value).length !== 4) return

  if (!mobileNumber.value) {
    toast({ message: "شماره موبایل مشخص نیست؛ لطفاً دوباره وارد شوید.", type: "error" })
    emit("back")
    return
  }

  loading.value = true
  try {
    const response = await verifyCode({ body: {
      mobileNumber: mobileNumber.value,
      code: otpNumber.value,
    } })

    if (!response || response.error?.value || !response.data?.value?.token) return

    const authData = response.data.value
    session.save(authData)
    emit("otpSuccess")
  }
  finally {
    loading.value = false
  }
}

const resendCode = async () => {
  if (!canResend.value || resendLoading.value) return
  resendLoading.value = true
  try {
    const response = await panelLogin({ body: { mobileNumber: mobileNumber.value } })
    if (!response || response.error?.value) return

    otpNumber.value = ""
    startTimer()
    toast({ message: "کد تأیید دوباره ارسال شد.", type: "success" })
  }
  finally {
    resendLoading.value = false
  }
}

onMounted(startTimer)
onBeforeUnmount(() => timer && clearInterval(timer))
</script>

<template>
  <VCard class="otp-step" flat>
    <div class="otp-step__brand"><img :src="mainLogo" alt="الومنشی"></div>

    <header>
      <span>تأیید شماره موبایل</span>
      <h2>کد تأیید را وارد کن</h2>
      <p>کد چهاررقمی ارسال‌شده به شماره زیر را وارد کن.</p>
      <div class="otp-step__phone" dir="ltr">
        <strong>{{ formattedMobile }}</strong>
        <button type="button" @click="emit('back')">ویرایش شماره</button>
      </div>
    </header>

    <VForm class="otp-step__form" @submit.prevent="submitForm">
      <AppOtpCodeInput
        v-model="otpNumber"
        :length="4"
        :disabled="loading"
        @complete="submitForm"
      />

      <div class="otp-step__resend">
        <span v-if="!canResend"><VIcon icon="tabler-clock" size="17" />ارسال مجدد تا {{ formattedTime }}</span>
        <button v-else type="button" :disabled="resendLoading" @click="resendCode">
          <VProgressCircular v-if="resendLoading" indeterminate size="16" width="2" />
          <VIcon v-else icon="tabler-refresh" size="17" />
          ارسال مجدد کد
        </button>
      </div>

      <VBtn
        block
        type="submit"
        color="primary"
        size="large"
        rounded="lg"
        :loading="loading"
        :disabled="loading || String(otpNumber).length !== 4"
        append-icon="tabler-check"
      >
        تأیید و ورود
      </VBtn>
    </VForm>

    <div class="otp-step__hint"><VIcon icon="tabler-info-circle" size="17" />ممکن است دریافت پیامک تا یک دقیقه زمان ببرد.</div>
  </VCard>
</template>

<style lang="scss" scoped>
.otp-step { width: min(410px,calc(100vw - 28px)); padding: 28px 25px 23px; border: 1px solid rgba(var(--v-theme-primary),.1); border-radius: 20px; background: #fff; color: rgba(var(--v-theme-on-surface),.88); box-shadow: 0 18px 50px rgba(42,34,78,.12); }
.otp-step__brand { display: grid; width: 62px; height: 62px; margin: 0 auto 16px; place-items: center; border-radius: 18px; background: linear-gradient(145deg,rgba(var(--v-theme-primary),.05),rgba(var(--v-theme-primary),.13)); }
.otp-step__brand img { width: 48px; height: 48px; object-fit: contain; }
.otp-step header { text-align: center; }
.otp-step header > span { color: rgb(var(--v-theme-primary)); font-size: 10px; font-weight: 700; }
.otp-step h2 { margin: 7px 0 5px; color: #302a42; font-size: 20px; }
.otp-step header p { margin: 0; color: rgba(var(--v-theme-on-surface),.53); font-size: 10px; }
.otp-step__phone { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 10px; }
.otp-step__phone strong { color: #342e48; font-size: 12px; letter-spacing: .7px; }
.otp-step__phone button { border: 0; background: transparent; color: rgb(var(--v-theme-primary)); cursor: pointer; font: inherit; font-size: 9px; font-weight: 600; }
.otp-step__form { display: grid; gap: 16px; margin-top: 23px; }
.otp-step__inputs { display: flex !important; width: 100%; max-width: 360px; margin: 0 auto; padding: 0; direction: ltr; justify-content: center !important; }
.otp-step__inputs :deep(.v-otp-input__content) { flex: 0 0 320px !important; width: 320px !important; max-width: 100% !important; height: 52px !important; box-sizing: border-box; justify-content: center !important; gap: 8px; padding: 0 !important; transform: translateX(4px); }
.otp-step__inputs :deep(.v-field) { flex: 0 0 46px !important; width: 46px !important; min-width: 46px !important; height: 48px !important; border-radius: 12px; }
.otp-step__inputs :deep(.v-field__field) { height: 48px !important; }
.otp-step__inputs :deep(.v-field__input) { min-width: 0; min-height: 48px !important; height: 48px !important; padding: 0 !important; text-align: center; }
.otp-step__inputs :deep(input) { min-width: 0; font-size: 17px !important; font-weight: 700; text-align: center; }
.otp-step__resend { display: flex; min-height: 28px; align-items: center; justify-content: center; color: rgba(var(--v-theme-on-surface),.48); font-size: 9px; }
.otp-step__resend span,.otp-step__resend button { display: flex; align-items: center; gap: 5px; }
.otp-step__resend button { border: 0; background: transparent; color: rgb(var(--v-theme-primary)); cursor: pointer; font: inherit; font-size: 9px; font-weight: 700; }
.otp-step__resend button:disabled { cursor: wait; opacity: .6; }
.otp-step__form :deep(.v-btn) { font-size: 12px; font-weight: 700; }
.otp-step__hint { display: flex; align-items: center; justify-content: center; gap: 5px; margin-top: 15px; padding-top: 14px; border-top: 1px solid rgba(var(--v-theme-on-surface),.07); color: rgba(var(--v-theme-on-surface),.43); font-size: 8px; }
@media (max-width:599px) { .otp-step { width: calc(100vw - 24px); max-width: 410px; padding: 23px 16px 19px; border-radius: 18px; } .otp-step__form { gap: 14px; margin-top: 20px; } .otp-step__inputs :deep(.v-otp-input__content) { flex-basis: 306px !important; width: 306px !important; gap: 6px; } }
@media (max-width:360px) { .otp-step { padding-inline:12px; } .otp-step__inputs :deep(.v-otp-input__content) { flex-basis: 272px !important; width: 272px !important; gap: 4px; transform: translateX(2px); } .otp-step__inputs :deep(.v-field) { flex-basis: 42px !important; width: 42px !important; min-width: 42px !important; } }
</style>
